import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Tenant, type Server, type Tier } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardBody } from '@/components/ui/card';
import { Badge, statusTone } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import { ChevronDown, ChevronsUpDown, ChevronUp, Plus } from 'lucide-react';
import { cn, formatDate, loginDaysLeft, relativeTime, removalDueLabel } from '@/lib/utils';

type SortKey = 'id' | 'server' | 'status' | 'tier' | 'auth' | 'sites' | 'health' | 'activity' | 'created' | 'customer';
type SortDir = 'asc' | 'desc';
type SortState = { key: SortKey; dir: SortDir };
type ColumnDef = { key: SortKey; label: string; thClass: string; defaultDir: SortDir };

// Column order + per-column default sort direction. The first click on a header
// sorts by its defaultDir (problems-first for status/auth/health — status puts
// tenants marked for removal on top, soonest due first — most-recent for
// activity/created, A→Z otherwise); clicking the active header again flips it.
const COLUMNS: ColumnDef[] = [
  { key: 'id', label: 'ID', thClass: 'px-5 py-3', defaultDir: 'asc' },
  { key: 'server', label: 'Server', thClass: 'px-3 py-3', defaultDir: 'asc' },
  { key: 'status', label: 'Status', thClass: 'px-3 py-3', defaultDir: 'asc' },
  { key: 'tier', label: 'Tier', thClass: 'px-3 py-3', defaultDir: 'asc' },
  { key: 'auth', label: 'Auth', thClass: 'px-3 py-3', defaultDir: 'asc' },
  { key: 'sites', label: 'Sites', thClass: 'px-3 py-3', defaultDir: 'asc' },
  { key: 'health', label: 'Health', thClass: 'px-3 py-3', defaultDir: 'asc' },
  { key: 'activity', label: 'Last active', thClass: 'px-3 py-3', defaultDir: 'desc' },
  { key: 'created', label: 'Created', thClass: 'px-3 py-3', defaultDir: 'desc' },
  { key: 'customer', label: 'Customer', thClass: 'px-3 py-3', defaultDir: 'asc' },
];

function SortHeader({
  col,
  sort,
  onSort,
}: {
  col: ColumnDef;
  sort: SortState | null;
  onSort: (key: SortKey, defaultDir: SortDir) => void;
}) {
  const active = sort?.key === col.key;
  const dir = active ? sort?.dir : undefined;
  return (
    <th
      className={col.thClass}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(col.key, col.defaultDir)}
        className="group inline-flex items-center gap-1 hover:text-slate-700"
      >
        {col.label}
        {active ? (
          dir === 'asc' ? (
            <ChevronUp size={12} />
          ) : (
            <ChevronDown size={12} />
          )
        ) : (
          <ChevronsUpDown size={12} className="text-slate-300 group-hover:text-slate-400" />
        )}
      </button>
    </th>
  );
}

export function TenantsPage() {
  const [dialogOpen, setDialogOpen] = useState(false);
  // Default to newest-first by creation date. The backend already returns rows
  // in this order, but seeding the sort state lights up the Created header
  // indicator and makes its first click flip to ascending rather than re-sort.
  const [sort, setSort] = useState<SortState | null>({ key: 'created', dir: 'desc' });
  const qc = useQueryClient();
  const tenants = useQuery({
    queryKey: ['tenants'],
    queryFn: () => api.get<{ tenants: Tenant[] }>('/api/tenants'),
    refetchInterval: 15_000,
  });

  // Tier slug → label lookup for the Tier column. Shares the ['tiers'] cache
  // with the Tiers page and the new-tenant dialog, so it's usually already warm.
  const tiersQuery = useQuery({
    queryKey: ['tiers'],
    queryFn: () => api.get<{ tiers: Tier[] }>('/api/tiers'),
  });
  const tierLabels = useMemo(
    () => new Map((tiersQuery.data?.tiers ?? []).map((t) => [t.id, t.label])),
    [tiersQuery.data]
  );

  const onSort = (key: SortKey, defaultDir: SortDir) =>
    setSort((prev) =>
      prev?.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: defaultDir }
    );

  const rows = useMemo(() => {
    const list = tenants.data?.tenants ?? [];
    if (!sort) return list;
    return [...list].sort((a, b) => compareTenants(a, b, sort.key, sort.dir, tierLabels));
  }, [tenants.data, sort, tierLabels]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Tenants</h1>
          <p className="text-sm text-slate-500 mt-1">Customer workloads — one container per tenant.</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="self-start sm:self-auto shrink-0">
          <Plus size={14} /> New tenant
        </Button>
      </div>

      <Card>
        <CardBody className="p-0">
          {tenants.isLoading ? (
            <div className="p-5 text-sm text-slate-500">Loading…</div>
          ) : !tenants.data?.tenants.length ? (
            <div className="p-8 text-center text-sm text-slate-500">
              No tenants yet. Provision one to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full min-w-[1040px] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  {COLUMNS.map((col) => (
                    <SortHeader key={col.key} col={col} sort={sort} onSort={onSort} />
                  ))}
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <TenantRow key={t.id} tenant={t} tierLabels={tierLabels} />
                ))}
              </tbody>
            </table>
            </div>
          )}
        </CardBody>
      </Card>

      <NewTenantDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  );
}

// Single glanceable liveness signal that merges the L3 container state
// (docker ps, reconciled by the 30s background poller) with the L7 health
// probe (HTTP GET /health, only run on demand from the tenant detail page).
// The container drives the dot; a failed probe downgrades a running box to
// amber. health_status is usually null (no probe run yet), so a running box
// with no probe reads green. Full breakdown lives in the hover tooltip.
function healthIndicator(t: Tenant): { dotClass: string; label: string; title: string } {
  const container = t.container_status;
  const probe = t.health_status;
  const title =
    `Container: ${container ?? 'unknown'}\n` +
    `Health probe: ${probe ?? 'not run yet'}\n` +
    `Updated ${t.last_health_at ? relativeTime(t.last_health_at) : 'never'}`;

  // Suspended is an intentional "off" — the Status column already says so, so
  // don't raise an alarming red dot for it. A payment hold keeps the container
  // running (only chat is blocked), so name it to explain the green-ish state.
  if (t.status === 'suspended') {
    return t.suspend_kind === 'payment_required'
      ? { dotClass: 'bg-amber-500', label: 'payment hold', title }
      : { dotClass: 'bg-slate-300', label: 'suspended', title };
  }
  if (container === 'missing' || container === 'stopped')
    return { dotClass: 'bg-red-500', label: container, title };
  if (container === 'running')
    return probe === 'unhealthy'
      ? { dotClass: 'bg-amber-500', label: 'not responding', title }
      : { dotClass: 'bg-emerald-500', label: 'running', title };
  return { dotClass: 'bg-slate-300', label: 'unknown', title };
}

// Compact provider auth tags for the list: one tag per provider whose
// credentials file is on the server, per the host-side sweep — "has this
// customer ever connected this provider", answered without touching a
// container. Red when credentials are still on disk but the login is over: for
// Claude the same sweep reads that from the login file, otherwise it's the last
// live check (detail page, probe button), which can be old. Amber with a day count
// when a Claude login ends within 3 days. No tag at all when nothing has ever been
// connected. This is the operator control plane, so naming the providers is fine.
type AuthTag = { label: string; tone: 'success' | 'warning' | 'danger'; title: string };
function authTags(t: Tenant): AuthTag[] {
  const out: AuthTag[] = [];
  const add = (
    label: string,
    present: boolean | null,
    status: Tenant['claude_oauth_status'],
    endsAt: string | null = null
  ) => {
    if (!present) return;
    const daysLeft = loginDaysLeft(endsAt);
    if (status === 'expired')
      out.push({
        label,
        tone: 'danger',
        title: `${label} login is on the server but has ended. The customer has to reconnect.`,
      });
    else if (daysLeft !== null)
      out.push({
        label: `${label} · ${daysLeft}d`,
        tone: 'warning',
        title: `${label} login ends ${formatDate(endsAt)}. The customer has to reconnect by then, or chats stop.`,
      });
    else
      out.push({
        label,
        tone: 'success',
        title: endsAt
          ? `${label} login ends ${formatDate(endsAt)}`
          : `${label} credentials present on the server`,
      });
  };
  add('Claude', t.claude_creds_present, t.claude_oauth_status, t.claude_login_expires_at);
  add('Codex', t.codex_creds_present, t.codex_oauth_status);
  return out;
}

// Sort rank for the derived Auth column: 0 = needs attention (a linked
// provider's login is over), 1 = a Claude login ending within 3 days, 2 =
// connected, 3 = nothing linked. Ascending surfaces the tenants an operator has
// to act on first.
function authRank(t: Tenant): number {
  const expired =
    (t.claude_creds_present && t.claude_oauth_status === 'expired') ||
    (t.codex_creds_present && t.codex_oauth_status === 'expired');
  if (expired) return 0;
  if (t.claude_creds_present && loginDaysLeft(t.claude_login_expires_at) !== null) return 1;
  if (t.claude_creds_present || t.codex_creds_present) return 2;
  return 3;
}

// Sort rank for the derived Health column, keyed off the same dot color
// healthIndicator picks so ascending puts the most alarming containers on top.
const HEALTH_RANK: Record<string, number> = {
  'bg-red-500': 0, // container missing / stopped
  'bg-amber-500': 1, // running but not responding
  'bg-emerald-500': 2, // healthy
  'bg-slate-300': 3, // suspended / unknown — intentionally off
};
function healthRank(t: Tenant): number {
  return HEALTH_RANK[healthIndicator(t).dotClass] ?? 3;
}

// Comparable value per column. Returns null for "no value on record" so the
// comparator can park those rows at the bottom.
function sortValue(
  t: Tenant,
  key: SortKey,
  tierLabels?: Map<string, string>
): string | number | null {
  switch (key) {
    case 'id':
      return t.id;
    case 'server':
      return t.server_id;
    case 'status':
      // Marked-for-removal first, soonest due first (ISO strings sort
      // chronologically), then by status name.
      return t.removal_marked_at && t.removal_due_at ? `0:${t.removal_due_at}` : `1:${t.status}`;
    case 'sites':
      return t.site_count;
    case 'tier':
      // Sort by the same label the cell shows (fall back to the raw slug);
      // null parks tenants with no tier at the bottom.
      return t.tier ? tierLabels?.get(t.tier) ?? t.tier : null;
    case 'auth':
      return authRank(t);
    case 'health':
      return healthRank(t);
    case 'activity': {
      if (!t.last_chat_activity_at) return null;
      const ms = Date.parse(t.last_chat_activity_at);
      return Number.isNaN(ms) ? null : ms;
    }
    case 'created': {
      const ms = Date.parse(t.created_at);
      return Number.isNaN(ms) ? null : ms;
    }
    case 'customer':
      return t.customer_email;
  }
}

function compareTenants(
  a: Tenant,
  b: Tenant,
  key: SortKey,
  dir: SortDir,
  tierLabels?: Map<string, string>
): number {
  const av = sortValue(a, key, tierLabels);
  const bv = sortValue(b, key, tierLabels);
  // Missing values (no last activity, no customer) always sort
  // to the bottom, regardless of the chosen direction.
  const aMissing = av === null || av === '';
  const bMissing = bv === null || bv === '';
  if (aMissing || bMissing) {
    if (aMissing && bMissing) return a.id.localeCompare(b.id);
    return aMissing ? 1 : -1;
  }
  const cmp =
    typeof av === 'number' && typeof bv === 'number'
      ? av - bv
      : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
  if (cmp !== 0) return dir === 'asc' ? cmp : -cmp;
  // Stable, direction-independent tiebreak so equal rows never jitter on refetch.
  return a.id.localeCompare(b.id);
}

function TenantRow({ tenant, tierLabels }: { tenant: Tenant; tierLabels: Map<string, string> }) {
  const health = healthIndicator(tenant);
  const auth = authTags(tenant);
  return (
    <tr className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
      <td className="px-5 py-3 font-mono text-xs">
        <Link to={`/tenants/${tenant.id}`} className="text-slate-900 hover:text-slate-700">
          {tenant.id}
        </Link>
      </td>
      <td className="px-3 py-3 font-mono text-xs text-slate-700">{tenant.server_id}</td>
      <td className="px-3 py-3">
        <div className="flex flex-wrap items-center gap-1">
          <Badge tone={statusTone(tenant.status)}>{tenant.status}</Badge>
          {tenant.never_delete && (
            <span title="Never delete is on — no sweep, mark or delete can remove this account">
              <Badge tone="neutral" className="whitespace-nowrap">
                never delete
              </Badge>
            </span>
          )}
          {tenant.removal_marked_at && tenant.removal_due_at && (
            <span title={`Marked for removal — due ${formatDate(tenant.removal_due_at)}`}>
              <Badge tone="danger" className="whitespace-nowrap">
                removal {removalDueLabel(tenant.removal_due_at)}
              </Badge>
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-3 text-slate-700">
        {tenant.tier ? (
          tierLabels.get(tenant.tier) ?? tenant.tier
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-3 py-3">
        {auth.length ? (
          <div className="flex flex-wrap gap-1">
            {auth.map((a) => (
              <span key={a.label} title={a.title}>
                <Badge tone={a.tone}>{a.label}</Badge>
              </span>
            ))}
          </div>
        ) : (
          <Badge tone="neutral">None</Badge>
        )}
      </td>
      <td className="px-3 py-3 text-xs text-slate-700">
        {tenant.site_count ?? (
          <span
            className="text-slate-400"
            title="Counted from the tenant's sites.json on the host; unknown only when it has no data directory there"
          >
            ?
          </span>
        )}
      </td>
      <td className="px-3 py-3">
        <span className="inline-flex items-center gap-2" title={health.title}>
          <span className={cn('h-2 w-2 rounded-full shrink-0', health.dotClass)} />
          <span className="text-xs text-slate-700">{health.label}</span>
        </span>
      </td>
      <td className="px-3 py-3 text-xs text-slate-500">
        {tenant.last_chat_activity_at ? (
          <span title={formatDate(tenant.last_chat_activity_at)}>
            {relativeTime(tenant.last_chat_activity_at)}
          </span>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-3 py-3 text-xs text-slate-500">
        <span title={formatDate(tenant.created_at)}>{relativeTime(tenant.created_at)}</span>
      </td>
      <td className="px-3 py-3 text-slate-700">{tenant.customer_email ?? '—'}</td>
      <td className="px-5 py-3 text-right">
        <Link
          to={`/tenants/${tenant.id}`}
          className="text-sm font-medium text-slate-600 hover:text-slate-900"
        >
          View
        </Link>
      </td>
    </tr>
  );
}

function NewTenantDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const servers = useQuery({
    queryKey: ['servers'],
    queryFn: () => api.get<{ servers: Server[] }>('/api/servers'),
    enabled: open,
  });
  const tiers = useQuery({
    queryKey: ['tiers'],
    queryFn: () => api.get<{ tiers: Tier[] }>('/api/tiers'),
    enabled: open,
  });

  const [form, setForm] = useState({
    id: '',
    server_id: '',
    customer_name: '',
    company_name: '',
    customer_email: '',
    notes: '',
    tier: '',
  });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post<{ tenant: Tenant }>('/api/tenants', {
        id: form.id,
        server_id: form.server_id || null,
        customer_name: form.customer_name || null,
        company_name: form.company_name || null,
        customer_email: form.customer_email || null,
        notes: form.notes || null,
        tier: form.tier || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenants'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      onOpenChange(false);
      setForm({ id: '', server_id: '', customer_name: '', company_name: '', customer_email: '', notes: '', tier: '' });
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="New tenant"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => create.mutate()} disabled={create.isPending || !form.id}>
            {create.isPending ? 'Provisioning…' : 'Create tenant'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>}
        <div>
          <Label htmlFor="id">Tenant ID (slug)</Label>
          <Input
            id="id"
            value={form.id}
            onChange={(e) => setForm({ ...form, id: e.target.value })}
            placeholder="acme"
          />
          <p className="text-xs text-slate-500 mt-1">
            3–32 chars, lowercase + hyphens. Becomes <code className="font-mono">&lt;id&gt;.&lt;region&gt;.wpgodmode.com</code>.
          </p>
        </div>
        <div>
          <Label htmlFor="server_id">Server</Label>
          <select
            id="server_id"
            className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
            value={form.server_id}
            onChange={(e) => setForm({ ...form, server_id: e.target.value })}
          >
            <option value="">Auto-pick (least loaded)</option>
            {servers.data?.servers
              .filter((s) => s.status === 'active')
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.id} — {s.tenant_count ?? 0}/{s.capacity}
                </option>
              ))}
          </select>
        </div>
        <div>
          <Label htmlFor="tier">Tier</Label>
          <select
            id="tier"
            className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
            value={form.tier}
            onChange={(e) => setForm({ ...form, tier: e.target.value })}
          >
            <option value="">Default tier</option>
            {tiers.data?.tiers.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
                {t.is_default ? ' (default)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="customer_name">Customer name</Label>
            <Input
              id="customer_name"
              value={form.customer_name}
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="company_name">Company name</Label>
            <Input
              id="company_name"
              value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })}
            />
          </div>
          <div>
            <Label htmlFor="customer_email">Customer email</Label>
            <Input
              id="customer_email"
              type="email"
              value={form.customer_email}
              onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
            />
          </div>
        </div>
        <div>
          <Label htmlFor="notes">Notes</Label>
          <Textarea
            id="notes"
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>
      </div>
    </Dialog>
  );
}
